<template>
    <!-- Playbook Creation Modal -->
    <div class="modal fade" id="playbook-creation-modal" tabindex="-1" aria-labelledby="playbook-creation-modal-label" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content playbook-creation-modal">
          <div class="modal-header border-0 pb-0"> 
            <h2 class="modal-title fw-bold text-dark mb-2 mx-2">{{ local_playbook.playbook_type?.name || 'Create New Playbook' }}</h2>
            <button type="button" class="btn btn-white float-end close-modal" data-bs-dismiss="modal" @click.prevent="closeModal()" data-bs-toggle="tooltip" title="Close"> 
              <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
            </button>
          </div>
  
          <div class="modal-body px-4 pb-0">
            <p v-if="local_playbook.playbook_type" class="text-muted mb-4">{{ local_playbook.playbook_type.description }}</p>
       
            <input type="text" id="play_name" v-model="local_playbook.name" 
                class="form-control mb-2" :class="{ 'is-invalid': errors.name }"
                placeholder="Name your play" aria-label="Name your play">
            <div v-if="errors.name" class="invalid-feedback d-block mb-2">{{ errors.name }}</div>
 
            <textarea id="play_description" v-model="local_playbook.description" 
                class="form-control mb-2" :class="{ 'is-invalid': errors.description }"
                rows="3" placeholder="Give us a sentence or two about your campaign. e.g. 'We are raising $100,000 to fund our new housing program.'"></textarea>
            <div v-if="errors.description" class="invalid-feedback d-block mb-2">{{ errors.description }}</div>

             <div class="row g-3 mb-4 align-items-md-center">
                 <div class="col-12 col-lg-auto">
                     <nav class="navbar navbar-expand tertiary-tab-bar flex-md-shrink-0 w-100">
                         <ul class="navbar-nav">
                         <li class="nav-item" style="pointer-events: none;">
                             <span class="nav-link d-flex flex-nowrap tertiary-tab" style="cursor: default !important; pointer-events: none;"><span>Base On</span></span>
                         </li>
                         <li class="nav-item">
                             <a
                                 class="nav-link d-flex flex-nowrap tertiary-tab"
                                 :class="{ 'tertiary-tab-active': date_type === 'start' }"
                                 href="#"
                                 @click.prevent="date_type = 'start'"
                             ><span>Start Date</span></a>
                         </li>
                         <li class="nav-item">
                             <a
                                 class="nav-link d-flex flex-nowrap tertiary-tab"
                                 :class="{ 'tertiary-tab-active': date_type === 'end' }"
                                 href="#"
                                 @click.prevent="date_type = 'end'"
                             ><span>End Date</span></a>
                         </li>
                         </ul>
                     </nav>   
                 </div>

                 <div class="col-12 col-lg">
                     <datepicker-utc 
                         v-model="local_playbook.date" 
                         input-class="form-control flex-grow-1 w-100"
                         placeholder="Select Date"
                     ></datepicker-utc>
                 </div>
             </div>
          </div>
          
          <div class="modal-footer px-4 pb-4">
            <button v-if="canEdit('playbooks')" type="button" class="btn btn-primary rounded-pill px-4 py-2" @click="createPlaybook()" :disabled="!local_playbook.name || !local_playbook.date">
              Next
            </button>
            <div v-else data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
              <button type="button" class="btn btn-primary rounded-pill px-4 py-2" :disabled="true">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { defineComponent } from 'vue'
  import permissionsMixin from '../../mixins/permissions';
  import { usePlaybookStore } from '../../stores/playbook';
  import { useClientStore } from '../../stores/client';
  import statsigService from '../../services/statsig';
  import { useNotifications } from '../../composables/useNotifications';
  
  export default defineComponent({
    name: 'PlaybookCreationModal',
    mixins: [permissionsMixin],
    emits: ['update:show','play-creating','play-created'],
    props: {
      show: {
        type: Boolean,
        default: false
      },
      playbook_type: {
        type: Object,
        default: null
      }
    },
  data() {
    return {
      local_playbook: {
        name: '',
        description: '',
        date: null,
        playbook_type: null
      },
      date_type: 'start',
      // Main modal
      creation_modal: null,
      modal_initialized: false,
      // Error handling
      errors: {
        name: '',
        description: '',
        date: '',
      }
    }
  },
    setup() {
      const playbookStore = usePlaybookStore();
      const clientStore = useClientStore();
      const { showNotification } = useNotifications();
      return {
        playbookStore,
        clientStore,
        showNotification
      };
    },
    computed: {
      client() {
        return this.clientStore.getClient;
      }
    },
    beforeMount() {
      if (this.playbook_type) {
        this.local_playbook.playbook_type = this.playbook_type;
      }
    },
    watch: {
      show: {
        handler(newVal) {
          if (newVal) {
            this.$nextTick(() => {
              // Ensure the local playbook type is current when opening
              if (this.playbook_type) {
                this.local_playbook.playbook_type = this.playbook_type;
              }
              // Initialize the creation modal when it's about to be shown
              if (!this.modal_initialized) {
                const modalElement = document.getElementById('playbook-creation-modal')
                if (modalElement) {
                  this.creation_modal = new bootstrap.Modal(modalElement)
                  
                  // Add event listener for when modal is hidden (only once)
                  modalElement.addEventListener('hidden.bs.modal', () => {
                    this.$emit('update:show', false)
                  })
                  
                  this.modal_initialized = true;
                } else {
                  console.error('Modal element not found!');
                }
              }
              this.showModal()
            })
          } else {
            this.hideModal()
          }
        },
        immediate: true
      },
      playbook_type: {
        handler(newPlaybookType, oldPlaybookType) {
          // Only update if the playbook_type actually changed (different ID)
          if (!oldPlaybookType || newPlaybookType.id !== oldPlaybookType.id) {
            this.local_playbook.playbook_type = newPlaybookType;
            this.local_playbook.name = '';
            this.local_playbook.description = '';
            this.local_playbook.date = null;
            this.date_type = 'start';
          }
        },
        immediate: true
      }
    },
    methods: {
      showModal() {
        if (this.creation_modal) {
          this.creation_modal.show()
        }
      },
      hideModal() {
        if (this.creation_modal) {
          this.creation_modal.hide()
        }
      },
      closeModal() {
        this.$emit('update:show', false)
      },
      async createPlaybook() {
        if (!this.local_playbook.name || !this.local_playbook.date || !this.local_playbook.playbook_type) {
          return;
        }
        
        // Clear previous errors
        this.errors = {
          name: '',
          description: '',
          date: '',
        };

        // Emit event with playbook ID to show loading screen
        this.$emit('play-creating', { 
          generating: true
        });
        
        try {
          const response = await axios.post(`/api/playbooks/${this.client.url}`, {
            name: this.local_playbook.name,
            description: this.local_playbook.description,
            playbook_type_id: this.local_playbook.playbook_type.id,
            date: this.local_playbook.date,
            date_type: this.date_type,
            status: 'draft'
          });
          
          const playbookId = response.data.playbook.id;
          
          // Calculate total assets from playbook type
          const totalAssets = response.data.playbook.assets?.length || 0;
          
          // Track playbook creation
          if (statsigService.getClient()) {
            statsigService.logEvent('playbook_created', null, {
              playbook_id: playbookId,
              playbook_type_id: this.local_playbook.playbook_type?.id,
              playbook_type_name: this.local_playbook.playbook_type?.name,
              playbook_name: this.local_playbook.name
            });
          }
          
          // Emit event with playbook ID to show loading screen
          this.$emit('play-created', {
            playbookId,
            playbookUrl: response.data.playbook.url,
            totalAssets
          });
          
          // Close the modal AFTER emitting the event
          if (this.creation_modal) {
            this.creation_modal.hide();
          }
          
        } catch (error) {
          if (error.response && error.response.status === 422) {
            // Handle validation errors
            const validationErrors = error.response.data.errors;
            Object.keys(validationErrors).forEach(field => {
              if (this.errors.hasOwnProperty(field)) {
                this.errors[field] = validationErrors[field][0];
              }
            });
            
            this.showNotification({
              text: 'Please correct the validation errors.',
              type: 'error'
            });
          } else {
            this.showNotification({
              text: 'An error occurred. Please try again.',
              type: 'error'
            });
          }
        }
      }
    }
  })
</script>